/* ─────────────────────────────────────────────────────────
 * CODE BLOCK LANGUAGES
 * The `Light` build of react-syntax-highlighter ships no grammars, so every
 * language the chat can render has to be registered here -- and each one is
 * bundled, so the list is deliberately finite.
 *
 * Anything not in this map is rendered as plain text by CodeBlock. That is on
 * purpose: react-syntax-highlighter falls back to `highlightAuto` for unknown
 * languages, which happily mis-colours a ```mermaid or ```diff block as
 * whichever registered grammar happens to score highest.
 * ───────────────────────────────────────────────────────── */
import { Light as SyntaxHighlighter } from "react-syntax-highlighter";

import js from "react-syntax-highlighter/dist/esm/languages/hljs/javascript";
import ts from "react-syntax-highlighter/dist/esm/languages/hljs/typescript";
import python from "react-syntax-highlighter/dist/esm/languages/hljs/python";
import php from "react-syntax-highlighter/dist/esm/languages/hljs/php";
import java from "react-syntax-highlighter/dist/esm/languages/hljs/java";
import csharp from "react-syntax-highlighter/dist/esm/languages/hljs/csharp";
import go from "react-syntax-highlighter/dist/esm/languages/hljs/go";
import rust from "react-syntax-highlighter/dist/esm/languages/hljs/rust";
import c from "react-syntax-highlighter/dist/esm/languages/hljs/c";
import cpp from "react-syntax-highlighter/dist/esm/languages/hljs/cpp";
import scala from "react-syntax-highlighter/dist/esm/languages/hljs/scala";
import kotlin from "react-syntax-highlighter/dist/esm/languages/hljs/kotlin";
import swift from "react-syntax-highlighter/dist/esm/languages/hljs/swift";
import objectivec from "react-syntax-highlighter/dist/esm/languages/hljs/objectivec";
import ruby from "react-syntax-highlighter/dist/esm/languages/hljs/ruby";
import perl from "react-syntax-highlighter/dist/esm/languages/hljs/perl";
import lua from "react-syntax-highlighter/dist/esm/languages/hljs/lua";
import haskell from "react-syntax-highlighter/dist/esm/languages/hljs/haskell";
import vbnet from "react-syntax-highlighter/dist/esm/languages/hljs/vbnet";
import vbscript from "react-syntax-highlighter/dist/esm/languages/hljs/vbscript";

import xml from "react-syntax-highlighter/dist/esm/languages/hljs/xml";
import css from "react-syntax-highlighter/dist/esm/languages/hljs/css";
import scss from "react-syntax-highlighter/dist/esm/languages/hljs/scss";
import json from "react-syntax-highlighter/dist/esm/languages/hljs/json";
import yaml from "react-syntax-highlighter/dist/esm/languages/hljs/yaml";
import markdown from "react-syntax-highlighter/dist/esm/languages/hljs/markdown";
import diff from "react-syntax-highlighter/dist/esm/languages/hljs/diff";

import bash from "react-syntax-highlighter/dist/esm/languages/hljs/bash";
import sql from "react-syntax-highlighter/dist/esm/languages/hljs/sql";

import matlab from "react-syntax-highlighter/dist/esm/languages/hljs/matlab";
import julia from "react-syntax-highlighter/dist/esm/languages/hljs/julia";
import r from "react-syntax-highlighter/dist/esm/languages/hljs/r";
import fortran from "react-syntax-highlighter/dist/esm/languages/hljs/fortran";
import mathematica from "react-syntax-highlighter/dist/esm/languages/hljs/mathematica";

import dockerfile from "react-syntax-highlighter/dist/esm/languages/hljs/dockerfile";
import nginx from "react-syntax-highlighter/dist/esm/languages/hljs/nginx";
import apache from "react-syntax-highlighter/dist/esm/languages/hljs/apache";
import powershell from "react-syntax-highlighter/dist/esm/languages/hljs/powershell";
import dos from "react-syntax-highlighter/dist/esm/languages/hljs/dos";
import cmake from "react-syntax-highlighter/dist/esm/languages/hljs/cmake";
import makefile from "react-syntax-highlighter/dist/esm/languages/hljs/makefile";
import ini from "react-syntax-highlighter/dist/esm/languages/hljs/ini";

type LanguageDefinition = Parameters<typeof SyntaxHighlighter.registerLanguage>[1];

/** Fence tag (and its aliases) -> grammar. Keys must be lowercase. */
const languages: Record<string, LanguageDefinition> = {
  javascript: js, js: js, jsx: js, mjs: js, cjs: js, node: js,
  typescript: ts, tsx: ts,
  python: python, py: python,
  php: php,
  java: java,
  csharp: csharp, cs: csharp, "c#": csharp,
  go: go, golang: go,
  rust: rust, rs: rust,
  c: c, h: c,
  cpp: cpp, "c++": cpp, cxx: cpp, hpp: cpp,
  scala: scala,
  kotlin: kotlin, kt: kotlin,
  swift: swift,
  objectivec: objectivec, objc: objectivec, "objective-c": objectivec,
  ruby: ruby, rb: ruby,
  perl: perl, pl: perl,
  lua: lua,
  haskell: haskell, hs: haskell,
  vbnet: vbnet, vb: vbnet,
  vbscript: vbscript, vbs: vbscript, vba: vbscript,

  xml: xml, html: xml, svg: xml, razor: xml, cshtml: xml, vbhtml: xml,
  css: css,
  scss: scss, sass: scss,
  json: json, jsonc: json,
  yaml: yaml, yml: yaml,
  markdown: markdown, md: markdown,
  diff: diff, patch: diff,

  bash: bash, sh: bash, shell: bash, zsh: bash,
  sql: sql,

  matlab: matlab, octave: matlab,
  julia: julia,
  r: r,
  fortran: fortran,
  mathematica: mathematica,

  dockerfile: dockerfile, docker: dockerfile,
  nginx: nginx,
  apache: apache,
  powershell: powershell, pwsh: powershell, ps1: powershell,
  dos: dos, batch: dos, bat: dos, cmd: dos,
  cmake: cmake,
  makefile: makefile, make: makefile,
  ini: ini, toml: ini, cfg: ini, conf: ini,
};

Object.entries(languages).forEach(([name, definition]) => {
  SyntaxHighlighter.registerLanguage(name, definition);
});

export const SUPPORTED_LANGUAGES = new Set(Object.keys(languages));
